<script setup lang="ts">
import { ref, computed, watch, h, type VNode } from 'vue';
import { NIcon, NTooltip, type DataTableColumns } from 'naive-ui';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import {
  IconCheckCircle,
  IconClose,
  IconDocument,
  IconDocumentText,
  IconList,
  IconPeople,
} from '@/components/design/icons';
import {
  type ContractManagementObligationInDb,
  type ContractManagementClauseInDb,
  type ContractManagementMatterInDb,
  searchContractManagementObligations,
  searchContractManagementClauses,
  searchContractManagementMatters,
  searchContractManagementContractualParties,
  type FilterableSearchQuery,
} from '@/api';
import type { ContractualParty } from '@/services/contractManagement/contractualPartyRow';
import { logger } from '@/utils/logger';
import { ACCESS_PERMISSION_WRITE, deniedReason } from '@/utils/accessPermission';
import type { EntityType } from '@/types/contractManagement/entityType';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

export type { EntityType };

/**
 * `assign`: classic contract-management flow — required contract-file/contract
 * context, emits just the UID (+ optional page number) for the caller to wire
 * up the assignment.
 *
 * `pick`: lightweight directory picker that runs without any contract context
 * and emits the full entity record so the caller can copy fields out of it
 * (used by the contract-templating wizard to populate template variables).
 */
export type SelectEntityModalMode = 'assign' | 'pick';

export type SelectableEntity =
  | ContractManagementObligationInDb
  | ContractManagementClauseInDb
  | ContractManagementMatterInDb
  | ContractualParty;

interface Props {
  show: boolean;
  entityType: EntityType;
  mode?: SelectEntityModalMode;
  // Contract file UID to assign the entity to (for static models, assign mode only)
  contractFileUid?: string;
  // Contract UID to assign the matter to (for matters, assign mode only)
  contractUid?: string;
  /** For `contractual-party`: list only parties of this kind. */
  partyKind?: 'natural_person' | 'legal_entity';
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'assigned', entityUid: string, pageNumber?: number): void;
  (e: 'selected', entity: SelectableEntity): void;
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'assign',
  contractFileUid: undefined,
  contractUid: undefined,
  partyKind: undefined,
});

const emit = defineEmits<Emits>();
const message = useGaiaToast();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

// Search and pagination state
const searchQuery = ref('');
const entities = ref<
  | ContractManagementObligationInDb[]
  | ContractManagementClauseInDb[]
  | ContractManagementMatterInDb[]
  | ContractualParty[]
>([]);
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const totalCount = ref(0);
const selectedEntity = ref<
  | ContractManagementObligationInDb
  | ContractManagementClauseInDb
  | ContractManagementMatterInDb
  | ContractualParty
  | null
>(null);
const pageNumber = ref<number | null>(0);
const hasAttemptedFetch = ref(false);

// Entity type configuration
const entityConfig = computed(() => {
  switch (props.entityType) {
    case 'obligation':
      return {
        name: 'Obligation',
        namePlural: 'Obligations',
        icon: IconList,
        color: '#f59e0b',
        searchFunction: searchContractManagementObligations,
        requiresContractFile: true,
        requiresPageNumber: true,
      };
    case 'clause':
      return {
        name: 'Clause',
        namePlural: 'Clauses',
        icon: IconDocument,
        color: '#ef4444',
        searchFunction: searchContractManagementClauses,
        requiresContractFile: true,
        requiresPageNumber: true,
      };
    case 'contractual-party':
      return {
        name: 'Contractual Party',
        namePlural: 'Contractual Parties',
        icon: IconPeople,
        color: '#0d9488',
        searchFunction: searchContractManagementContractualParties,
        requiresContractFile: true,
        requiresPageNumber: false,
      };
    case 'matter':
      return {
        name: 'Matter',
        namePlural: 'Matters',
        icon: IconDocumentText,
        color: '#2563eb',
        searchFunction: searchContractManagementMatters,
        requiresContractFile: false,
        requiresPageNumber: false,
      };
    default:
      throw new Error(`Unknown entity type: ${props.entityType}`);
  }
});

// Debounced search
// The search field owns the debounce and fires this at once when it is cleared.
function applySearch(): void {
  currentPage.value = 1;
  void fetchEntities();
}

/**
 * In `assign` mode the modal is meaningless without a contract-file / contract
 * context, so we gate the fetch on that. `pick` mode is purely a directory
 * search and runs without any contract context.
 */
function hasContractContext(): boolean {
  if (props.mode === 'pick') return true;
  return entityConfig.value.requiresContractFile
    ? Boolean(props.contractFileUid)
    : Boolean(props.contractUid);
}

// Fetch entities
async function fetchEntities(): Promise<void> {
  if (!showModal.value || !hasContractContext()) {
    return;
  }

  loading.value = true;
  hasAttemptedFetch.value = true;
  try {
    const query: FilterableSearchQuery = {
      text: searchQuery.value || '',
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: 'created_at',
      sort_direction: 'desc',
      similarity_threshold: 0.5,
      column_filters: props.partyKind ? { kind: [props.partyKind] } : {},
    };

    // For matters, pass exclude_contract_uids to exclude already linked matters;
    // parties already on this file are left out the same way.
    const apiOptions =
      props.entityType === 'matter' && props.contractUid
        ? {
            body: query,
            query: {
              exclude_contract_uids: [props.contractUid],
            },
          }
        : props.entityType === 'contractual-party' && props.contractFileUid
          ? { body: query, query: { exclude_contract_file_uids: [props.contractFileUid] } }
          : { body: query };

    const response = await entityConfig.value.searchFunction(apiOptions);

    if (!response.data) {
      throw new Error(`Failed to search ${entityConfig.value.namePlural.toLowerCase()}`);
    }

    // Use server-side pagination for all entity types (backend handles filtering)
    entities.value = response.data.results;
    totalCount.value = response.data.result_count;
  } catch (error) {
    logger.error(`Error fetching ${entityConfig.value.namePlural.toLowerCase()}:`, error);
    const errorMsg =
      error instanceof Error
        ? error.message
        : `Failed to load ${entityConfig.value.namePlural.toLowerCase()}`;
    message.error(errorMsg);
    entities.value = [];
    totalCount.value = 0;
  } finally {
    loading.value = false;
  }
}

// Handle entity selection
/**
 * Linking a party to a contract file changes the party too, so the backend
 * wants WRITE on it — a party the caller only views is listed but can't be
 * assigned. Picking (copying values out) needs READ only.
 */
function assignBlock(entity: { uid: string; permissions?: number | null }): string | null {
  if (props.mode !== 'assign' || props.entityType !== 'contractual-party') return null;
  return deniedReason(entity.permissions, ACCESS_PERMISSION_WRITE);
}

function handleEntitySelect(entity: (typeof entities.value)[0]): void {
  if (assignBlock(entity)) return;
  selectedEntity.value = entity;
  pageNumber.value = 0;
}

const searchInput = ref<{ focus: () => void } | null>(null);

/**
 * Picking from the keyboard: the arrows move the highlighted row while the
 * caret stays in the search box, and Enter takes it. An Enter typed before
 * the debounced search has run is a search, not a pick — the highlight still
 * belongs to the previous results.
 */
function handleSearchKeydown(event: KeyboardEvent): void {
  const list = entities.value;
  if (event.key === 'Enter') {
    const typed = event.target instanceof HTMLInputElement ? event.target.value : '';
    if (selectedEntity.value && typed === searchQuery.value) {
      event.preventDefault();
      handleConfirm();
    }
    return;
  }
  if ((event.key !== 'ArrowDown' && event.key !== 'ArrowUp') || list.length === 0) return;
  event.preventDefault();
  const current = list.findIndex(entity => entity.uid === selectedEntity.value?.uid);
  const step = event.key === 'ArrowDown' ? 1 : -1;
  const next =
    current === -1
      ? step === 1
        ? 0
        : list.length - 1
      : Math.min(Math.max(current + step, 0), list.length - 1);
  handleEntitySelect(list[next]);
}

// Handle confirm — branches on mode: `assign` keeps the legacy contract-file
// wiring (UID + optional page number); `pick` hands the full entity record
// back to the caller so it can copy fields out of it.
function handleConfirm(): void {
  if (!selectedEntity.value) {
    message.warning(`Please select a ${entityConfig.value.name.toLowerCase()}`);
    return;
  }

  if (props.mode === 'pick') {
    emit('selected', selectedEntity.value);
    showModal.value = false;
    return;
  }

  // For matters, don't pass pageNumber; for static models, pass it
  if (entityConfig.value.requiresPageNumber) {
    const pageNum = pageNumber.value !== null && pageNumber.value >= 0 ? pageNumber.value : 0;
    emit('assigned', selectedEntity.value.uid, pageNum);
  } else {
    emit('assigned', selectedEntity.value.uid);
  }
  showModal.value = false;
}

// Handle cancel/close
function handleCancel(): void {
  showModal.value = false;
}

// Reset modal state
function resetModal(): void {
  searchQuery.value = '';
  currentPage.value = 1;
  selectedEntity.value = null;
  pageNumber.value = 0;
  entities.value = [];
  totalCount.value = 0;
  hasAttemptedFetch.value = false;
}

// Track if we're in the initial fetch to prevent page watch from triggering
const isInitialFetch = ref(false);

// Watch for modal opening and props - fetch immediately when modal opens with valid props
watch(
  [
    showModal,
    (): EntityType | undefined => props.entityType,
    (): string | undefined => props.contractFileUid,
    (): string | undefined => props.contractUid,
  ],
  async ([newShowValue, newEntityType]): Promise<void> => {
    if (newShowValue && newEntityType && hasContractContext()) {
      // Reset state first
      isInitialFetch.value = true;
      searchQuery.value = '';
      currentPage.value = 1;
      selectedEntity.value = null;
      pageNumber.value = 0;
      entities.value = [];
      totalCount.value = 0;
      hasAttemptedFetch.value = false;

      // Fetch entities immediately
      await fetchEntities();
      isInitialFetch.value = false;
    } else if (!newShowValue) {
      // Reset when modal closes
      resetModal();
      isInitialFetch.value = false;
    }
  },
  { immediate: true }
);

// Watch for page changes - only fetch if modal is open and not during initial fetch
watch(currentPage, () => {
  if (currentPage.value > 0 && showModal.value && hasContractContext() && !isInitialFetch.value) {
    void fetchEntities();
  }
});

watch(pageSize, () => {
  if (pageSize.value > 0 && showModal.value && hasContractContext() && !isInitialFetch.value) {
    currentPage.value = 1;
    void fetchEntities();
  }
});

// Table columns
const columns = computed(
  (): DataTableColumns<
    ContractManagementObligationInDb | ContractManagementClauseInDb | ContractManagementMatterInDb
  > => {
    const baseColumns: DataTableColumns<
      ContractManagementObligationInDb | ContractManagementClauseInDb | ContractManagementMatterInDb
    > = [
      {
        title: 'Name',
        key: 'name',
        ellipsis: {
          tooltip: true,
        },
        render: (
          row:
            | ContractManagementObligationInDb
            | ContractManagementClauseInDb
            | ContractManagementMatterInDb
        ): VNode => {
          const displayName =
            props.entityType === 'clause'
              ? // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
                (row as ContractManagementClauseInDb).title || 'Untitled Clause'
              : 'name' in row
                ? row.name || 'Untitled'
                : 'Untitled';
          return h('div', { style: 'display: flex; align-items: center; gap: 8px; min-width: 0' }, [
            h(
              NIcon,
              { size: 16, color: entityConfig.value.color, style: 'flex-shrink: 0' },
              {
                default: () => h(entityConfig.value.icon),
              }
            ),
            h(
              NTooltip,
              { placement: 'top', trigger: 'hover' },
              {
                trigger: () =>
                  h(
                    'span',
                    {
                      style: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0,
                      },
                    },
                    displayName
                  ),
                default: () => displayName,
              }
            ),
          ]);
        },
      },
    ];

    // Add type column for obligations (not for matters)
    if (props.entityType === 'obligation') {
      baseColumns.push({
        title: 'Type',
        key: 'type',
        width: 150,
        render: (
          row:
            | ContractManagementObligationInDb
            | ContractManagementClauseInDb
            | ContractManagementMatterInDb
        ): VNode => {
          const obligation = row as ContractManagementObligationInDb;
          return h('span', obligation.type);
        },
      });
    }

    // Add category column for clauses
    if (props.entityType === 'clause') {
      baseColumns.push({
        title: 'Category',
        key: 'category',
        width: 150,
        render: (
          row:
            | ContractManagementObligationInDb
            | ContractManagementClauseInDb
            | ContractManagementMatterInDb
        ): VNode => {
          const clause = row as ContractManagementClauseInDb;
          return h('span', clause.category);
        },
      });
    }

    // Add description column (if available for this entity type)
    baseColumns.push({
      title: 'Description',
      key: 'description',
      ellipsis: {
        tooltip: true,
      },
      render: (
        row:
          | ContractManagementObligationInDb
          | ContractManagementClauseInDb
          | ContractManagementMatterInDb
      ): VNode => {
        // Some entity types might not have description
        const description =
          'description' in row
            ? (row as ContractManagementObligationInDb | ContractManagementMatterInDb).description
            : null;
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        return h('span', description || '—');
      },
    });

    return baseColumns;
  }
);

// Row props (make rows clickable)
const rowProps = (
  row:
    ContractManagementObligationInDb | ContractManagementClauseInDb | ContractManagementMatterInDb
): Record<string, unknown> => {
  const blocked = assignBlock(row);
  return {
    style: blocked ? { cursor: 'not-allowed', opacity: 0.55 } : { cursor: 'pointer' },
    title: blocked ?? undefined,
    onClick: (): void => {
      handleEntitySelect(row);
    },
    class: selectedEntity.value?.uid === row.uid ? 'selected-row' : '',
  };
};

// Selected entity keys
const selectedEntityKeys = computed(() => {
  if (!selectedEntity.value) return [];
  return [selectedEntity.value.uid];
});

// Handle row selection
function handleRowSelection(keys: (string | number)[]): void {
  if (keys.length === 0) {
    selectedEntity.value = null;
    return;
  }
  const entityUid = keys[0] as string;
  const entity = entities.value.find(e => e.uid === entityUid);
  if (entity && !assignBlock(entity)) {
    selectedEntity.value = entity;
    pageNumber.value = 0;
  }
}

// Pagination config
const paginationConfig = computed(
  (): {
    page: number;
    pageSize: number;
    itemCount: number;
    showSizePicker: boolean;
    pageSizes: number[];
    onUpdatePage: (page: number) => void;
    onUpdatePageSize: (size: number) => void;
  } => ({
    page: currentPage.value,
    pageSize: pageSize.value,
    itemCount: totalCount.value,
    showSizePicker: true,
    pageSizes: [10, 20, 50],
    onUpdatePage: (page: number): void => {
      currentPage.value = page;
    },
    onUpdatePageSize: (size: number): void => {
      pageSize.value = size;
      currentPage.value = 1;
    },
  })
);
</script>

<template>
  <NModal
    v-model:show="showModal"
    preset="card"
    :title="
      mode === 'pick'
        ? `Pick a ${entityConfig.name}`
        : entityConfig.requiresContractFile
          ? `Assign ${entityConfig.name} to Contract File`
          : `Select ${entityConfig.name} to Assign`
    "
    style="width: 750px; max-width: 90vw"
    :mask-closable="false"
    class="select-entity-modal"
    @after-enter="searchInput?.focus()"
  >
    <div class="modal-content">
      <div class="header-section">
        <div class="header-title">
          <NIcon :size="24" :style="{ color: entityConfig.color }">
            <component :is="entityConfig.icon" />
          </NIcon>
          <NText strong style="font-size: 18px">
            {{
              mode === 'pick'
                ? `Pick an existing ${entityConfig.name}`
                : `Select ${entityConfig.name} to Assign`
            }}
          </NText>
        </div>
        <NText depth="3" style="font-size: 13px">
          <template v-if="mode === 'pick'">
            Choose a {{ entityConfig.name.toLowerCase() }} from the list below to use its data.
          </template>
          <template v-else>
            Choose a {{ entityConfig.name.toLowerCase() }} from the list below to assign it to this
            {{ entityConfig.requiresContractFile ? 'contract file' : 'contract' }}.
          </template>
        </NText>
      </div>

      <div class="search-section">
        <gaia-search-input
          ref="searchInput"
          v-model:value="searchQuery"
          variant="panel"
          size="medium"
          :placeholder="`Search ${entityConfig.namePlural.toLowerCase()}`"
          :debounce="400"
          :on-keydown="handleSearchKeydown"
          @search="applySearch"
        />
      </div>

      <div class="table-section">
        <gaia-table
          :columns="columns"
          :data="entities"
          :loading="loading"
          :remote="true"
          :pagination="paginationConfig"
          :row-key="row => row.uid"
          :row-props="rowProps"
          :checked-row-keys="selectedEntityKeys"
          flex-height
          style="height: 100%"
          @update:checked-row-keys="handleRowSelection"
        >
          <template #empty>
            <NEmpty
              v-if="hasAttemptedFetch"
              :description="`No ${entityConfig.namePlural.toLowerCase()} found`"
              style="padding: 40px 0"
            />
          </template>
        </gaia-table>
      </div>

      <transition name="fade-slide">
        <div v-if="selectedEntity" class="selection-confirmation">
          <div class="selected-card">
            <div class="selected-info">
              <div class="icon-wrapper" :style="{ backgroundColor: entityConfig.color + '20' }">
                <NIcon :size="20" :style="{ color: entityConfig.color }">
                  <component :is="entityConfig.icon" />
                </NIcon>
              </div>
              <div class="text-details">
                <NText strong class="selection-name">
                  {{
                    props.entityType === 'clause'
                      ? (selectedEntity as ContractManagementClauseInDb).title || 'Untitled Clause'
                      : 'name' in selectedEntity
                        ? selectedEntity.name
                        : 'Untitled'
                  }}
                </NText>
                <div class="selection-meta">
                  <NText depth="3" class="selection-desc">
                    {{
                      'type' in selectedEntity
                        ? selectedEntity.type
                        : 'category' in selectedEntity
                          ? selectedEntity.category
                          : entityConfig.name
                    }}
                  </NText>
                </div>
              </div>
            </div>
            <NButton quaternary circle size="small" @click="selectedEntity = null">
              <template #icon>
                <NIcon><IconClose /></NIcon>
              </template>
            </NButton>
          </div>

          <div v-if="entityConfig.requiresPageNumber && mode === 'assign'" class="form-section">
            <NForm label-placement="left" :show-feedback="false">
              <NFormItem label="Assign to Page:">
                <gaia-input-number
                  v-model:value="pageNumber"
                  :min="0"
                  placeholder="0"
                  style="width: 100px"
                />
                <NText depth="3" style="font-size: 12px; margin-left: 12px">
                  (Optional) Specify page number within the document
                </NText>
              </NFormItem>
            </NForm>
          </div>
        </div>
      </transition>
    </div>

    <template #footer>
      <NSpace justify="end">
        <NButton @click="handleCancel">Cancel</NButton>
        <NButton
          type="primary"
          :disabled="!selectedEntity"
          style="min-width: 140px"
          @click="handleConfirm"
        >
          <template #icon>
            <NIcon><IconCheckCircle /></NIcon>
          </template>
          {{ mode === 'pick' ? `Use this ${entityConfig.name}` : `Assign ${entityConfig.name}` }}
        </NButton>
      </NSpace>
    </template>
  </NModal>
</template>

<style scoped>
.modal-content {
  display: flex;
  flex-direction: column;
  height: 65vh;
  gap: 16px;
}

.header-section {
  flex-shrink: 0;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.search-section {
  flex-shrink: 0;
}

.table-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #f3f4f6;
  border-radius: 4px;
}

.selection-confirmation {
  flex-shrink: 0;
  padding: 16px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.selected-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0; /* Allows children to truncate */
}

.icon-wrapper {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.text-details {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.selection-name {
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;
}

.selection-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.selection-desc {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-section {
  padding-top: 12px;
  border-top: 1px dashed #d1d5db;
}

:deep(.n-data-table) {
  flex: 1;
}

:deep(.n-data-table .n-data-table-tbody tr.selected-row) {
  background-color: #eff6ff;
}

:deep(.n-data-table .n-data-table-tbody tr.selected-row:hover) {
  background-color: #dbeafe;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
